import type { World } from '../sim/types';
import { CHAPTERS, CHAPTER_NAMES, OBJECTIVES, ONGOING, currentObjective, guideSkipped, passed, reviewWhen, type ChapterId } from '../sim/campaign';
import { dateLabel } from '../sim/calendar';
import { dollars } from '../sim/operations';
import { escapeHtml } from './format';
import type { CardView } from './overviewPanels';
import { monthSummaryHtml } from './areasPanel';

/**
 * The campaign as the Manage pane shows it (M239): its chapters, each objective done or not and why it matters, and the
 * first annual review once it is held. Markup only, from the world.
 */

/** The campaign's line at the head of Manage, under the operations: the chapter and the objective of the moment. */
export function campaignSummaryHtml(world: World): string {
  // None where the sanctuary has no reef: the campaign is the reef's (M239's review).
  if (!world.sanctuaries[0]?.coral || !world.sanctuaries[0]?.fish) return '';
  const now = currentObjective(world);
  const c = world.campaign;
  const text = now ? `Chapter ${CHAPTERS.indexOf(now.chapter) + 1} · ${CHAPTER_NAMES[now.chapter]}: ${now.objective.title}` : c.reviews.length === 1 ? 'The first year reviewed. The sanctuary is yours to run.' : c.reviews.length > 1 ? `${c.reviews.length} years reviewed. The next review comes on ${reviewWhen(c)}.` : 'The year\'s work done: its review comes on its last day.';
  return `<section class="campaign-summary"><span class="ops-label">Maui Nui: The Reef at a Crossroads</span><span>${escapeHtml(text)}</span>
    <button data-act="open" data-kind="campaign">The campaign</button>${c.reviews.length > 0 ? '<button data-act="open" data-kind="review">The annual review</button>' : ''}</section>`;
}

/** The campaign's page: each chapter, its objectives each done or not, the one of the moment marked. */
export function campaignHtml(world: World): string {
  const c = world.campaign;
  const now = currentObjective(world);
  const chapter = (ch: ChapterId, i: number): string => {
    // The guide put away closes its chapter on the day's tick; shown closed at once (M303's second review).
    const skipped = ch === 'firstSurvey' && guideSkipped(c);
    const state = i < c.chapter || skipped ? 'done' : i === c.chapter ? 'now' : 'later';
    const begun = c.chapterDays[i];
    const items = OBJECTIVES[ch].map((o) => {
      const day = c.done[o.id];
      const current = now?.objective.id === o.id;
      // A guide's step passed or put away, told as such: neither done nor waiting (M303's second review).
      const aside = day !== undefined || ch !== 'firstSurvey' ? null : passed(c, o.id) ? 'Passed: you went on to the steps after it' : skipped ? 'Put away with the guide' : null;
      return `<li class="objective-item${day !== undefined ? ' met' : ''}${current ? ' current' : ''}"><span class="tick" aria-hidden="true">${day !== undefined ? '✓' : aside ? '–' : current ? '›' : '○'}</span><span><strong>${escapeHtml(o.title)}</strong><span class="row-note">${escapeHtml(day !== undefined ? `Done ${dateLabel(day)}` : aside ?? o.why)}</span></span></li>`;
    }).join('');
    return `<li class="chapter ${state}"><h4>Chapter ${i + 1}: ${escapeHtml(CHAPTER_NAMES[ch])}${begun !== undefined ? ` <span class="row-note">from ${escapeHtml(dateLabel(begun))}</span>` : ''}</h4><ul class="objectives">${items}</ul></li>`;
  };
  return `<h3>Maui Nui: The Reef at a Crossroads</h3>
    <p class="why">The conservancy's first year at Maui Nui. Objectives are met by what the conservancy does, in any order; the reef will not recover in a year, and is not asked to. ${c.reviews.length > 0 ? 'The next' : 'The first'} annual review is held on ${escapeHtml(reviewWhen(c))}.</p>
    <ol class="chapters">${CHAPTERS.map(chapter).join('')}</ol>
    ${c.reviews.length > 0 ? `<p class="note">${escapeHtml(ONGOING)}</p>` : ''}`;
}

const GRADE_WORDS = { good: 'Good', fair: 'Fair', poor: 'Poor' } as const;

/** The latest annual review's page: its year at a glance (M287), its six readings and its grant (M240: one a year). */
export function reviewHtml(world: World): string {
  const r = world.campaign.reviews.at(-1);
  if (!r) return `<h3>The annual review</h3><p class="note">Held on ${escapeHtml(reviewWhen(world.campaign))}.</p>`;
  // The year it reviews (M287, `docs/CONSERVATION_PROGRESS.md` 8.1): from the review before it, or the campaign's start.
  const before = world.campaign.reviews.at(-2);
  const from = before ? before.day + 1 : (world.campaign.chapterDays[0] ?? r.day);
  const level = (g: 'good' | 'fair' | 'poor'): string => (g === 'good' ? 'good' : g === 'fair' ? 'fair' : 'poor');
  return `<h3>${world.campaign.reviews.length === 1 ? 'The first annual review' : `The ${dateLabel(r.day).slice(-4)} annual review`}</h3>
    <p class="why">Held ${escapeHtml(dateLabel(r.day))}. ${r.award > 0 ? `Its performance grant: ${dollars(r.award)}, ${dollars(r.award / Math.max(1, r.lines.filter((l) => l.grade === 'good').length))} for each reading of good.` : 'No reading was good enough for a performance grant.'}</p>
    ${monthSummaryHtml(world, from, r.day, 'year')}
    <h4>The review's readings</h4>
    <ul class="rows">${r.lines.map((l) => `<li class="manage-row"><span class="row-name">${escapeHtml(l.label)}</span><span class="level level-${level(l.grade)}">${GRADE_WORDS[l.grade]}</span><span class="row-note">${escapeHtml(l.text)}</span></li>`).join('')}</ul>
    <p class="note">${escapeHtml(ONGOING)}</p>`;
}

/** The mark a page opened makes for the campaign (M239): the coral's monitoring read, or (M262) a survey's findings. */
export function markOf(view: CardView): 'readCoral' | 'readThreats' | null {
  if (view.kind === 'coral' || (view.kind === 'species' && view.id === 'coral') || (view.kind === 'indicator' && view.id === 'reef')) return 'readCoral';
  // The findings (M262), once a survey is home: the HUD marks them only then (`Hud.markPage`).
  if (view.kind === 'findings') return 'readThreats';
  return null;
}
